import { colors, sxButton, sxFont_16_400, sxFont_18_500 } from "@/theme";
import {
  formatAxiosError,
  isFunction,
  isNonEmptyArray,
  isNonEmptyObject,
  isNonEmptyString
} from "@/utils";
import {
  Add,
  SaveAlt
} from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridRowModesModel,
  GridRowsProp,
  GridSlotProps,
  GridToolbarContainer
} from "@mui/x-data-grid";
import {
  forwardRef,
  useImperativeHandle
} from "react";
import AlertDialog from "../AlertDialog";
import { getColumnActions } from "./columnActions";
import { DataGridDefaultStyle, pageSize } from "./constants";
import { CrudGridProps, CrudGridRef } from "./interface";
import * as S from "./styles";
import useCrudGrid from "./useCrudGrid";
import useEditToolBar from "./useEditToolBar";
import CustomTable from "./CustomTable";
import React, { useState } from "react";


declare module "@mui/x-data-grid" {
  interface ToolbarPropsOverrides {
    setRows: (newRows: (oldRows: GridRowsProp) => GridRowsProp) => void;
    setRowModesModel: (
      newModel: (oldModel: GridRowModesModel) => GridRowModesModel
    ) => void;
    newRecord?: any;
    fieldToFocus?: string;
    newRecordId: number;
    exportFn?: () => void;
  }
}


function EditToolbar(props: GridSlotProps["toolbar"]) {
  const editorTools = useEditToolBar(props)
  if (!editorTools) return
  const { handleAddRecordClick, handleExportClick, exportFn, newRecord } = editorTools

  return (
    <GridToolbarContainer>
      {isNonEmptyObject(newRecord) ? (
        <Button
          color="primary"
          startIcon={<Add />}
          onClick={handleAddRecordClick}
        >
          Add record
        </Button>
      ) : null}
      {isFunction(exportFn) ? (
        <Button
          color="primary"
          startIcon={<SaveAlt />}
          onClick={handleExportClick}
        >
          Export
        </Button>
      ) : null}
    </GridToolbarContainer>
  );
}

const CrudGridTest = forwardRef<CrudGridRef, CrudGridProps>(
  (
    props,
    ref
  ) => {
    const {
      baseUrl,
      path,
      additionalURLSearchParams,
      columns,
      getRowId,
      setRowId,
      header,
      newRecord,
      fieldToFocus,
      additionalAction,
      newRecordId = -1,
      exportFn,
      initialState,
      onRowClick,
      search: _search,
      onEditRow,
      setTotalRowCount: _setTotalRowCount,
      withoutActionColumn,
      checkboxSelection,
      rowDropdownIcon,
      searchPlaceHolder,
      shouldShowDelete = true,
      onRowsChange,
    } = props

    console.log(shouldShowDelete)

    const [sortModel, setSortModel] = useState<{ field: string; sort: 'asc' | 'desc' }[]>([]);

    const { rowModesModel, rows, handleSaveClick,
      handleCancelClick,
      handleEditClick,
      handleDeleteClick,
      totalRowCount,
      isDeleteDialogOpen,
      searchInput,
      setSearchInput,
      onPaginationModelChange,
      onFilterModelChange,
      onSortModelChange,
      onSortModelChange: useCrudGridSortModelChange,
      processRowUpdate,
      handleRowModesModelChange,
      handleRowEditStop,
      handleDeleteDialogClosed,
      reloadData,
      deleteSelectedRows,
      onSearchChange,
      apiRef,
      isLoading,
      paginationModel,
      setRows,
      setRowModesModel,
      error
    } = useCrudGrid({ ...props, sortModel });

    const columnActions = getColumnActions({
      _setTotalRowCount, rowModesModel, rows, handleSaveClick,
      handleCancelClick,
      handleEditClick,
      checkboxSelection,
      handleDeleteClick,
      additionalAction,
      shouldShowDelete
    })
    // Ensure all columns have disableColumnMenu: true
    const columnsWithDisableMenu = (columns || []).map(col => ({
      ...col,
      disableColumnMenu: col.disableColumnMenu !== undefined ? col.disableColumnMenu : true,
    }));
    const columnsWithActions = [
      ...columnsWithDisableMenu,
      ...(!withoutActionColumn ? [columnActions] : []),
    ];

    // Add dropdown column if rowDropdownIcon is provided
    const dropdownColumn = rowDropdownIcon
      ? ([{
        field: 'dropdown',
        headerName: '',
        width: 48,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params: any) => rowDropdownIcon(params.row),
        align: 'center',
        headerAlign: 'center',
      }] as GridColDef[])
      : [];

    useImperativeHandle(ref, () => ({
      reloadData: reloadData,
      deleteSelectedRows: deleteSelectedRows,
    }));

    // Call onRowsChange whenever rows change
    React.useEffect(() => {
      if (typeof onRowsChange === 'function') {
        onRowsChange(Array.from(rows));
      }
    }, [rows, onRowsChange]);

    if (!isNonEmptyString(baseUrl) || !isNonEmptyArray(columns)) {
      return null;
    }

    const countedSum = rows.reduce((sum, row) => {
      // Sum all counted values in the nested array for this row
      const rowCounted = Array.isArray(row.inventoryCountWithInventoryCountProducts)
        ? row.inventoryCountWithInventoryCountProducts.reduce((subSum, prod) => subSum + (prod.counted ?? 0), 0)
        : (row.counted ?? 0);
      return sum + rowCounted;
    }, 0);

    const expectedSum = rows.reduce((sum, row) => sum + (row.inventoryCount ?? 0), 0);

    const discrepancySum = rows.reduce((sum, row) => {
      // If discrepancy is not directly available, calculate it as (rowCounted - expected)
      const rowCounted = Array.isArray(row.inventoryCountWithInventoryCountProducts)
        ? row.inventoryCountWithInventoryCountProducts.reduce((subSum, prod) => subSum + (prod.counted ?? 0), 0)
        : (row.counted ?? 0);
      const expected = row.inventoryCount ?? 0;
      // If row.discrepancy is available and correct, you can use it instead
      return sum + (row.discrepancy ?? (rowCounted - expected));
    }, 0);

    return (
      <S.CrudGridContainer>
        {!!header ? <S.CrudGridHeader>Test {header}</S.CrudGridHeader> : null}
        <Box sx={{ background: colors.white, padding: 4, borderRadius: '10px' }}>
          <TextField
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              onSearchChange(e.target.value);
            }}
            placeholder={searchPlaceHolder || "Search by item name or sku"}
            variant="outlined"
            size="small"
            sx={{
              mb:2,
              width: "25%",
              background: "#fff",
              borderRadius: "24px",
              '& .MuiOutlinedInput-root': {
                borderRadius: '50px',
                fontSize: '14px',
                paddingRight: '8px',
                py:1
              },
              '& .MuiInputAdornment-root': {
                color: '#A0AEC0',
              },
              boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <CustomTable
            columns={[...columnsWithActions, ...dropdownColumn]}
            rows={Array.from(rows)}
            getRowId={getRowId || ((row) => row.id)}
            page={paginationModel?.page + 1}
            pageSize={paginationModel?.pageSize}
            rowCount={totalRowCount}
            onPageChange={(event, newPage) => {
              onPaginationModelChange({ ...paginationModel, page: newPage - 1 });
            }}
            loading={isLoading}
            sortModel={sortModel}
            onSortModelChange={(model) => {
              setSortModel(model);
            }}
            onRefresh={reloadData}
          />
        </Box>
        <Dialog
          open={isDeleteDialogOpen}
          onClose={() => handleDeleteDialogClosed()}
          aria-labelledby="crud-grid-delete-confirm-dialog-title"
          aria-describedby="crud-grid-delete-confirm-dialog-description"
          sx={{ px: 10, pt: 2 }}
        >
          <DialogTitle id="crud-grid-delete-confirm-dialog-title">
            <Typography sx={{ ...sxFont_18_500, color: colors.neutralGrey800 }}>
              Delete
            </Typography>
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="crud-grid-delete-confirm-dialog-description">
              <Typography
                sx={{ ...sxFont_16_400, color: colors.neutralGrey700 }}
              >
                Are you sure?  <br />
                This action is not reversable!
              </Typography>
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ m: "auto", mb: 2 }}>
            <Button onClick={() => handleDeleteDialogClosed()} variant="outlined" sx={{ ...sxButton, bgcolor: "transparent", color: colors.doveGray, borderRadius: "8px", px: 2 }}>
              Cancel
            </Button>
            <Button
              onClick={() => handleDeleteDialogClosed(true)}
              autoFocus
              sx={{ ...sxButton, bgcolor: colors.red, borderRadius: "8px", px: 2 }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
        {error ? (
          <AlertDialog
            message={formatAxiosError(error)}
            title="Error"
            confirmText="Ok"
            severity="error"
          />
        ) : null}
      </S.CrudGridContainer>
    );
  }
);

CrudGridTest.displayName = "CrudGridTest";
export default CrudGridTest;
